import React from 'react';
import Layout from '../components/Layout';
import { FaGraduationCap, FaBriefcase, FaCloud, FaCode, FaDatabase, FaBrain } from 'react-icons/fa';
import '../styles/About.css';
import { ReactComponent as Logo } from '../assets/logo.svg';

export default function About() {
  return (
    <Layout>
      <div className="about-container">
        <div className="about-content">
          
          <div className="hero-section">
            <div className="hero-logo-wrapper">
              <Logo className="hero-logo" />
            </div>
            <h1 className="hero-title">Marcus Webb</h1>
            <p className="hero-subtitle">Lead MLOps & Platform Engineer</p>
            <p className="hero-description">
              Proven Machine Learning Ops and Platform Engineer with 8+ years of experience designing, building, and scaling cloud-native ML infrastructure. Deep expertise in shipping reusable CI/CD platforms, automating workflow deployments, and optimizing compute costs across AWS (SageMaker), GCP (Vertex AI, GKE), and Azure. Skilled in bridging data science prototyping with secure, robust production workloads.
            </p>
          </div>

          <h2 className="section-title">Technical Expertise</h2>
          <div className="skills-grid">
            <div className="skill-card">
              <div className="skill-icon-wrapper blue">
                <FaCloud className="skill-icon" />
              </div>
              <h3 className="skill-title">Cloud & Platforms</h3>
              <ul className="skill-list">
                {['Amazon Web Services', 'Google Cloud (GCP)', 'Microsoft Azure', 'SageMaker', 'Vertex AI', 'Kubernetes (GKE)', 'Docker'].map((tech) => (
                  <li key={tech} className="skill-item">
                    <span className="skill-bullet blue"></span>
                    {tech}
                  </li>
                ))}
              </ul>
            </div>

            <div className="skill-card">
              <div className="skill-icon-wrapper indigo">
                <FaCode className="skill-icon" />
              </div>
              <h3 className="skill-title">Languages & CI/CD</h3>
              <ul className="skill-list">
                {['Python', 'Java', 'Scala', 'SQL', 'GitHub Actions', 'GitLab CI', 'Terraform'].map((tech) => (
                  <li key={tech} className="skill-item">
                    <span className="skill-bullet indigo"></span>
                    {tech}
                  </li>
                ))}
              </ul>
            </div>

            <div className="skill-card">
              <div className="skill-icon-wrapper purple">
                <FaDatabase className="skill-icon" />
              </div>
              <h3 className="skill-title">Data Engineering</h3>
              <ul className="skill-list">
                {['Databricks (DAB)', 'Apache Spark', 'Apache Airflow', 'Google BigQuery', 'Snowflake', 'Power BI', 'MATLAB'].map((tech) => (
                  <li key={tech} className="skill-item">
                    <span className="skill-bullet purple"></span>
                    {tech}
                  </li>
                ))}
              </ul>
            </div>

            <div className="skill-card">
              <div className="skill-icon-wrapper emerald">
                <FaBrain className="skill-icon" />
              </div>
              <h3 className="skill-title">MLOps & Analytics</h3>
              <ul className="skill-list">
                {['PyTorch', 'MLflow', 'Scikit-Learn', 'Model Registries', 'DDP Distributed Training', 'FinOps Governance', 'JMP'].map((tech) => (
                  <li key={tech} className="skill-item">
                    <span className="skill-bullet emerald"></span>
                    {tech}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <h2 className="section-title">Professional Timeline</h2>
          <div className="timeline">
            <div className="timeline-line"></div>
            
            {/* Datavant */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle blue">
                  <FaBriefcase className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Senior ML Ops Engineer</h3>
                    <p className="timeline-company blue">Datavant</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">04/2025 - Present</p>
                    <p className="timeline-date">Remote</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Architected and shipped a reusable SageMaker CI/CD platform (ml-common-ci, ml-sagemaker-images, template repos) utilizing GitHub Actions, ECR vulnerability scan gating, and MLflow model-registry integration.</li>
                    <li>Built Datavant's Databricks Asset Bundle CI/CD framework (DAB) across workspaces, featuring automated per-PR bundle deployment and cleanup, composite actions, and Claude AI-powered PR reviews and deployment diagnostics.</li>
                    <li>Led SageMaker infrastructure buildout in Terraform Cloud: VPC interface endpoints, NCCL traffic configs for multi-node distributed training, and single-node multi-GPU DDP training architectures.</li>
                    <li>Implemented cloud security hardening: remediated API token leaks, migrated containers to secure Chainguard bases, resolved Wiz/CVE scan failures (Java 17, Jetty), and tightened workspace permissions.</li>
                    <li>Owned MLOps FinOps: built AWS & Databricks cost-reporting dashboards, designed automated weekly cost reporting, and shipped a Slack ChatOps bot for GPU capacity-reservation governance.</li>
                    <li>Delivered and maintained production ML inference pipelines (ICD detection, batch inference, latency alerting) and served as primary enablement lead unblocking Data Science teams.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Clarvos */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle blue">
                  <FaBriefcase className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Lead Machine Learning Ops Engineer</h3>
                    <p className="timeline-company blue">Clarvos</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">06/2024 - 04/2025</p>
                    <p className="timeline-date">Remote</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Led the design and implementation of ML systems for social media analytics, forecasting, and LLM-based applications.</li>
                    <li>Promoted to Sr. Full Stack Engineer and Tech Lead after 9 months, increasing responsibility to React frontend and Java Spring Boot backend systems.</li>
                    <li>Developed standardized MLOps pipelines leveraging Airflow, BigQuery, and Spark to deliver social media perception insights.</li>
                    <li>Provisioned GCP resources including Kubernetes (GKE), Vertex AI Pipelines and Registries, and Airflow instances.</li>
                    <li>Built CI/CD pipelines (GitLab CI) supporting automated packaging and testing of Python and Java ML modules.</li>
                    <li>Deployed performance monitoring dashboards using Looker and Power BI to track and evaluate model drift and quality.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Kohl's */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle blue">
                  <FaBriefcase className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Lead Machine Learning Engineer</h3>
                    <p className="timeline-company blue">Kohl's</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">06/2022 - 06/2024</p>
                    <p className="timeline-date">Remote</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Managed and acted as individual contributor on the ML Engineering team responsible for design and scaling of ML solutions across search, recommendation engines, and ads.</li>
                    <li>Maintained GKE and Airflow MLOps infrastructure alongside Kafka streaming data pipelines.</li>
                    <li>Collaborated with Data Engineering leads to define the ML roadmap, provisioning Cloud resources (BigQuery, BigTable, Cloud Functions) for model serving.</li>
                    <li><strong>Key Project:</strong> Successfully deployed a customer uplift model on Kohls.com targeting persuadable customers with limited-time offers in real-time, powering live personalization.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* PointsBet */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle blue">
                  <FaBriefcase className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Machine Learning Engineer</h3>
                    <p className="timeline-company blue">PointsBet</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">08/2021 - 06/2022</p>
                    <p className="timeline-date">Denver, CO</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Designed, implemented, and maintained customer-facing recommendation and acquisition ML services in Azure.</li>
                    <li>Promoted to Team Lead after 6 months, managing 3 direct reports.</li>
                    <li>Built Databricks ETL and GitLab CI/CD pipelines to convert research notebooks into robust, cloud-native services.</li>
                    <li><strong>Key Project:</strong> Deployed a real-time trending search algorithm onto the PointsBet Web/App leveraging a streaming Kernel Density Estimation (KDE) model.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Dow Chemical */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle blue">
                  <FaBriefcase className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Statistician & ML Engineer</h3>
                    <p className="timeline-company blue">Dow Chemical Co.</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">08/2017 - 08/2021</p>
                    <p className="timeline-date">Freeport, TX</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Developed enterprise-scale cloud-native ML and analytics solutions in Azure for time-series, video, and image manufacturing data.</li>
                    <li>Transitioned into a newly-created MLE role in 2020, implementing CI/CD pipelines via Azure DevOps.</li>
                    <li>Advised business partners on experimental designs (MSA, Gage R&R, Round Robins).</li>
                    <li><strong>Key Project:</strong> Deployed a transfer-learning Computer Vision model based on the YOLO architecture to detect manufacturing safety infractions in real-time.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* SFA State University */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle purple">
                  <FaGraduationCap className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Assistant Professor - Mathematics</h3>
                    <p className="timeline-company purple">Stephen F. Austin State University</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">08/2015 - 08/2017</p>
                    <p className="timeline-date">Nacogdoches, TX</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <ul>
                    <li>Tenure-track role consisting of mathematics research, publishing, and undergraduate lecturing.</li>
                    <li>Published original results in academic research journals and presented at national mathematics conferences.</li>
                    <li>Taught all courses across the undergraduate mathematics curriculum, authoring custom course textbooks and lecture materials.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Ph.D. Nebraska */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle purple">
                  <FaGraduationCap className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">Ph.D. Mathematics</h3>
                    <p className="timeline-company purple">University of Nebraska</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">2010 - 2015</p>
                    <p className="timeline-date">Lincoln, NE</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <p>Advanced doctoral research in mathematical theory, algebra, and analytics, providing the formal statistical and computational foundations for subsequent work in machine learning and distributed systems.</p>
                </div>
              </div>
            </div>

            {/* MS Texas State */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle purple">
                  <FaGraduationCap className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">M.S. Mathematics</h3>
                    <p className="timeline-company purple">Texas State University</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">2008 - 2010</p>
                    <p className="timeline-date">San Marcos, TX</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <p>Master's degree focusing on advanced applied and theoretical mathematics, computational models, and statistics.</p>
                </div>
              </div>
            </div>

            {/* BS Texas A&M */}
            <div className="timeline-item">
              <div className="timeline-icon-wrapper">
                <div className="timeline-icon-circle purple">
                  <FaGraduationCap className="timeline-icon" />
                </div>
              </div>
              <div className="timeline-card">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-title">B.S. Mathematics</h3>
                    <p className="timeline-company purple">Texas A&M University</p>
                  </div>
                  <div className="timeline-meta">
                    <p className="timeline-date">2003 - 2007</p>
                    <p className="timeline-date">College Station, TX</p>
                  </div>
                </div>
                <div className="timeline-description">
                  <p>Bachelor of Science in Mathematics. Solidified foundations in linear algebra, calculus, and mathematical modeling.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
}